import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Paper,
  Divider,
} from '@mui/material';
import { getPgtOrders } from './pgtOrder.helper';
import { usePatientContext } from '../patinetProvider';
import { getButtonStates } from '../../utils/permissionService';
import GradingUnavailable from '../../pages/Embryology/Occyte-Grading/oocyteGradingUnavailable';  
import { useTranslation } from 'react-i18next';

export default function PgtOrders() {
  const { t: tGeneral } = useTranslation('general');
  const { t: tPGT } = useTranslation('pgt_config');
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  const { patientId } = usePatientContext();

  const { canView } = getButtonStates('Lab', 'PGT', 'Order', 'female');
  
  useEffect(() => {
    const fetchOrders = async () => {
      if (!patientId) return;
      setLoading(true);
      try {
        const response = await getPgtOrders(patientId, 'PROGENESIS');
        const objects = response.objects || [];
        // Calculate rank based on ai_score (highest = #1)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const withScore = objects.map((o: any, idx: number) => ({
          ...o,
          _ai_score_num: o.ai_score !== undefined && o.ai_score !== null && o.ai_score !== '' ? Number(o.ai_score) : null,
          _originalIndex: idx,
        }));
        const sorted = [...withScore].sort((a, b) => {
          if (a._ai_score_num === null && b._ai_score_num === null) return 0;
          if (a._ai_score_num === null) return 1;
          if (b._ai_score_num === null) return -1;
          return b._ai_score_num - a._ai_score_num;
        });
        const rankMap = new Map();
        let rank = 1;
        for (const o of sorted) {
          if (o._ai_score_num !== null) {
            rankMap.set(o._originalIndex, `#${rank} Euploid Normal`);
            rank++;
          } else {
            rankMap.set(o._originalIndex, '-');
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const withRank = withScore.map((o: any) => {
          const { _originalIndex, ...rest } = o;
          return { ...rest, rank: rankMap.get(_originalIndex) };
        });
        setOrders(withRank);
      } catch (error) {
        setOrders([]);
        // Optionally log or show error
        // eslint-disable-next-line no-console
        console.error('Failed to fetch PGT orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [patientId]);

  if (!canView) {
    return <GradingUnavailable message={tGeneral('youDoNotHaveAccessToViewPage', { defaultValue: 'You do not have access to view this page.' })} />;
  }

  return (
    <Card sx={{ p: 1, boxShadow: 'none', border: 'none' }}>
      <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[ 
                    tPGT('embryoNo', 'Embryo No.'),
                    tPGT('dateTime', 'Date'),
                    tGeneral('day', 'Day'),
                    tGeneral('stage', 'Stage'),
                    // tGeneral('grade', 'Grade'),
                    // tPGT('aiGrade', 'AI Grade'),
                    tPGT('aiScore', 'AI Score'),
                    // tPGT('incubator', 'Incubator'),
                    // tPGT('pgtType', 'PGT Type'),
                    tPGT('facility', 'Facility'),
                    // tPGT('plan', 'Plan'),
                    tPGT('Biopsy By', 'Biopsy By'),
                    tPGT('witness', 'Witness'),
                    tPGT('rank', 'Rank'),
                  ].map((header, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        color: '#333',
                        whiteSpace: 'nowrap',
                        padding: '10px 16px',
                        borderBottom: '1px solid #ccc',
                        lineHeight: 2.5,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {tPGT('noOrdersFound', 'No PGT Orders found.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((item, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                          transform: 'scale(1.005)',
                          boxShadow: `0 2px 4px ${theme.palette.action.hover}`,
                        },
                        '& td': {
                          fontSize: '0.85rem',
                          padding: '10px 16px',
                          lineHeight: 2.5,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <TableCell>{item.embryo_no}</TableCell>
                      <TableCell>
                        {item.pgt_order?.pgt_order_date || '-'} 
                      </TableCell>
                      <TableCell>{item?.day ?? '-'}</TableCell>
                      <TableCell>{item?.stage_label ? item.stage_label : (item.stage ?? '-')}</TableCell>
                      {/* <TableCell>{item.grade ?? '-'}</TableCell> */}
                      {/* <TableCell>{item.ai_grade ?? '-'}</TableCell>  */}
                      <TableCell>{item?.ai_score ?? '-'}</TableCell>
                      {/* <TableCell>{item.incubator ?? '-'}</TableCell> */}
                      {/* <TableCell>{item.pgt_order?.pgt_type ?? '-'}</TableCell> */}
                      <TableCell>{item.pgt_order?.pgt_facility ?? '-'}</TableCell>
                      {/* <TableCell>{capitalizeTitle(item.action) ?? '-'}</TableCell> */}
                      <TableCell>{item?.embryologist ?? '-'}</TableCell>
                      <TableCell>{item?.witness ?? '-'}</TableCell>
                      <TableCell>{item?.rank ?? '-'}</TableCell>
                    </TableRow> 
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
